import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { 
  Search, Bell, Settings, LogOut, Plus, ChevronDown, 
  Users, FileText, Building, Calculator, Calendar, Mail,
  CreditCard, User, Shield, HelpCircle, Home
} from 'lucide-react';
// Keine externe Suchbibliothek mehr nötig

const pathMap = {
  'dashboard': 'Dashboard',
  'applicants': 'Bewerber',
  'applicant-details': 'Bewerberdetails',
  'partners': 'Partner',
  'partner-details': 'Partnerdetails',
  'statements': 'Abrechnungen',
  'settings': 'Einstellungen'
};

const quickActions = [
  {
    title: "Bewerber & Personal",
    items: [
      { name: 'Neuer Bewerber', path: '/applicants/add', icon: User, permission: 'create:applicants' },
      { name: 'Neue Stelle', path: '/jobs/add', icon: FileText, permission: 'create:jobs' },
      { name: 'Neuer Mitarbeiter', path: '/employees/add', icon: Users, permission: 'create:employees' }
    ]
  },
  {
    title: "Partner & Kunden",
    items: [
      { name: 'Neuer Partner', path: '/partners/add', icon: Building, permission: 'create:partners' },
      { name: 'Neuer Kunde', path: '/customers/add', icon: Users, permission: 'create:customers' },
      { name: 'Neue Location', path: '/locations/add', icon: Building, permission: 'create:locations' }
    ]
  },
  {
    title: "Finanzen",
    items: [
      { name: 'Neue Abrechnung', path: '/statements/add', icon: Calculator, permission: 'create:statements' },
      { name: 'Neue Rechnung', path: '/invoices/add', icon: FileText, permission: 'create:invoices' },
      { name: 'Neue Zahlung', path: '/payments/add', icon: CreditCard, permission: 'create:payments' }
    ]
  }
];

const profileMenuSections = [
  {
    title: 'Profil & Account',
    items: [
      { name: 'Profil bearbeiten', path: '/profile', icon: User, permission: 'edit:profile' },
      { name: 'Account Einstellungen', path: '/account', icon: Settings, permission: 'manage:account' },
      { name: 'Sicherheit', path: '/security', icon: Shield, permission: 'manage:security' }
    ]
  },
  {
    title: 'Support & Hilfe',
    items: [
      { name: 'Dokumentation', path: '/docs', icon: FileText, permission: 'read:docs' },
      { name: 'Support kontaktieren', path: '/support', icon: Mail, permission: 'contact:support' },
      { name: 'Hilfe & FAQs', path: '/help', icon: HelpCircle, permission: 'read:help' }
    ]
  }
];

// Icon mapping function for breadcrumbs
const getBreadcrumbIcon = (path) => {
  const iconMap = {
    'dashboard': Home,
    'applicants': Users,
    'partners': Building,
    'statements': Calculator,
    'settings': Settings
  };
  return iconMap[path] || Home;
};

const TopNav = () => {
  const { user, logout, getAccessTokenSilently } = useAuth0();
  const [userPermissions, setUserPermissions] = useState([]);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Refs for each dropdown container
  const searchRef = useRef(null);
  const searchInputRef = useRef(null);
  const notificationsRef = useRef(null);
  const userMenuRef = useRef(null);
  const quickActionsRef = useRef(null);

  // Handle permissions loading
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const token = await getAccessTokenSilently();
        const decodedToken = JSON.parse(atob(token.split('.')[1]));
        const permissions = decodedToken.permissions || [];
        setUserPermissions(permissions);
      } catch (error) {
        console.error('Error getting permissions:', error);
      }
    };

    checkPermissions();
  }, [getAccessTokenSilently]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown === 'search' && searchRef.current && !searchRef.current.contains(event.target)) {
        setActiveDropdown(null);
      } else if (activeDropdown === 'notifications' && notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setActiveDropdown(null);
      } else if (activeDropdown === 'userMenu' && userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setActiveDropdown(null);
      } else if (activeDropdown === 'quickActions' && quickActionsRef.current && !quickActionsRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    // Close active dropdown on escape key
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && activeDropdown) {
        setActiveDropdown(null);
      }
    };

    // Close dropdowns when route changes
    const handleRouteChange = () => {
      setActiveDropdown(null);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscKey);
    window.addEventListener('popstate', handleRouteChange);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, [activeDropdown]);

  // Permission check helper
  const hasPermission = (requiredPermission) => {
    if (!requiredPermission) return true;
    return userPermissions.includes(requiredPermission);
  };

  // Check if section has any permitted items
  const hasAccessToSection = (section) => {
    return section.items.some(item => hasPermission(item.permission));
  };

  // Check if user has access to any quick actions
  const hasAnyQuickActions = () => {
    return quickActions.some(section => hasAccessToSection(section));
  };

  // Generate breadcrumbs
  const breadcrumbs = React.useMemo(() => {
    return location.pathname
      .split('/')
      .filter(Boolean)
      .reduce((acc, path, index, array) => {
        const to = `/${array.slice(0, index + 1).join('/')}`;
        const isID = !isNaN(path);
        
        if (isID && acc.length > 0) {
          const lastBreadcrumb = {...acc[acc.length - 1]};
          lastBreadcrumb.name += ` #${path}`;
          lastBreadcrumb.to = to;
          return [...acc.slice(0, acc.length - 1), lastBreadcrumb];
        } else if (!isID) {
          return [...acc, {
            name: pathMap[path] || path.charAt(0).toUpperCase() + path.slice(1),
            to,
            icon: getBreadcrumbIcon(path)
          }];
        }
        
        return acc;
      }, []);
  }, [location.pathname]);

  // Simuliere Suchergebnisse - in der realen Implementierung würdest du hier deine API abfragen
  const performSearch = (query) => {
    setIsSearching(true);
    
    // Simuliere API-Verzögerung
    setTimeout(() => {
      if (!query.trim()) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }
      
      // Demo-Daten - ersetze dies durch deine eigene Suchlogik oder API-Aufruf
      const demoData = [
        { id: 1, firstName: 'Max', lastName: 'Mustermann', email: 'max.mustermann@example.com', type: 'applicant' },
        { id: 2, firstName: 'Anna', lastName: 'Schmidt', email: 'anna.schmidt@example.com', type: 'applicant' },
        { id: 3, firstName: 'Thomas', lastName: 'Weber', email: 'thomas.weber@example.com', type: 'employee' },
        { id: 4, firstName: 'Laura', lastName: 'Meyer', email: 'laura.meyer@example.com', type: 'partner' },
        { id: 5, firstName: 'Michael', lastName: 'Becker', email: 'michael.becker@example.com', type: 'customer' },
        { id: 6, firstName: 'Restaurant', lastName: 'Adler', email: 'info@adler-restaurant.com', type: 'location' },
      ];
      
      // Filtere basierend auf der Suchabfrage (Groß-/Kleinschreibung ignorieren)
      const lowerQuery = query.toLowerCase();
      const results = demoData.filter(item => {
        return item.firstName.toLowerCase().includes(lowerQuery) || 
               item.lastName.toLowerCase().includes(lowerQuery) || 
               item.email.toLowerCase().includes(lowerQuery);
      });
      
      setSearchResults(results);
      setIsSearching(false);
    }, 300);
  };
  
  // Führe eine Suche durch und speichere sie in den letzten Suchen
  const handleSearch = (query) => {
    setSearchQuery(query);
    performSearch(query);
    
    // Füge die Suche zu den letzten Suchen hinzu, wenn sie nicht leer ist
    if (query.trim()) {
      // Verhindere Duplikate und behalte nur die letzten 5 Suchen
      setRecentSearches(prev => {
        const updated = prev.filter(item => item !== query);
        return [query, ...updated].slice(0, 5);
      });
    }
  };
  
  // Wähle ein Suchergebnis aus und navigiere entsprechend
  const handleSelectSearchResult = (result) => {
    setActiveDropdown(null);
    
    // Navigiere basierend auf dem Typ des Ergebnisses
    switch(result.type) {
      case 'applicant':
        navigate(`/applicants/${result.id}`);
        break;
      case 'employee':
        navigate(`/employees/${result.id}`);
        break;
      case 'partner':
        navigate(`/partners/${result.id}`);
        break;
      case 'customer':
        navigate(`/customers/${result.id}`);
        break;
      case 'location':
        navigate(`/locations/${result.id}`);
        break;
      default:
        navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };
  
  // Bei Auswahl einer letzten Suche diese in das Suchfeld setzen und ausführen
  const selectRecentSearch = (query) => {
    setSearchQuery(query);
    performSearch(query);
    
    // Fokussiere das Suchfeld
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };
  
  // Toggle a dropdown menu
  const toggleDropdown = (name) => {
    const isOpening = activeDropdown !== name;
    setActiveDropdown(activeDropdown === name ? null : name);
    
    // Wenn die Suche geöffnet wird, fokussiere das Suchfeld
    if (name === 'search' && isOpening) {
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    }
  };

  return (
    <nav className="bg-white border-b border-gray-100 sticky top-0 z-50">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6">
        <div className="flex justify-between h-16">
          {/* Left section - Breadcrumbs */}
          <div className="flex items-center space-x-4 overflow-x-auto">
            {breadcrumbs.map((breadcrumb, index) => (
              <React.Fragment key={breadcrumb.to}>
                {index > 0 && (
                  <span className="text-gray-300">
                    <ChevronDown className="w-5 h-5 rotate-[-90deg]" />
                  </span>
                )}
                <Link
                  to={breadcrumb.to}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-md transition-colors whitespace-nowrap
                    ${index === breadcrumbs.length - 1
                      ? 'text-gray-900 bg-gray-50 font-medium'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                >
                  <breadcrumb.icon className="w-4 h-4" />
                  <span>{breadcrumb.name}</span>
                </Link>
              </React.Fragment>
            ))}
          </div>

          {/* Right section */}
          <div className="flex items-center space-x-1">
            {/* Quick Actions Button */}
            {hasAnyQuickActions() && (
              <div className="relative" ref={quickActionsRef}>
                <button 
                  className={`p-2 rounded-md transition-colors ${
                    activeDropdown === 'quickActions' 
                      ? 'text-gray-900 bg-gray-100' 
                      : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                  onClick={() => toggleDropdown('quickActions')}
                  aria-expanded={activeDropdown === 'quickActions'}
                  aria-haspopup="true"
                >
                  <Plus className="w-5 h-5" />
                </button>
                
                {activeDropdown === 'quickActions' && (
                  <div className="absolute right-0 mt-2 w-[480px] bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                    <div className="grid grid-cols-1 divide-y divide-gray-100">
                      {quickActions.map((section, idx) => (
                        hasAccessToSection(section) && (
                          <div key={idx} className="p-4">
                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                              {section.title}
                            </h3>
                            <div className="grid grid-cols-2 gap-2">
                              {section.items.map((item, itemIdx) => (
                                hasPermission(item.permission) && (
                                  <button
                                    key={itemIdx}
                                    onClick={() => {
                                      navigate(item.path);
                                      setActiveDropdown(null);
                                    }}
                                    className="flex items-center space-x-3 p-2 rounded-md hover:bg-gray-50 transition-colors w-full text-left"
                                  >
                                    <div className="flex-shrink-0 w-8 h-8 bg-blue-50 text-blue-600 rounded-md flex items-center justify-center">
                                      <item.icon className="w-4 h-4" />
                                    </div>
                                    <span className="text-sm font-medium text-gray-900">
                                      {item.name}
                                    </span>
                                  </button>
                                )
                              ))}
                            </div>
                          </div>
                        )
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Search */}
            {hasPermission('read:search') && (
              <div className="relative" ref={searchRef}>
                <button 
                  className={`p-2 rounded-md transition-colors ${
                    activeDropdown === 'search' 
                      ? 'text-gray-900 bg-gray-100' 
                      : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                  onClick={() => toggleDropdown('search')}
                  aria-expanded={activeDropdown === 'search'}
                  aria-haspopup="true"
                >
                  <Search className="w-5 h-5" />
                </button>
                
                {activeDropdown === 'search' && (
                  <div className="absolute right-0 mt-2 w-[480px] bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                    <div className="p-4">
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Search className="h-4 w-4 text-gray-400" />
                        </div>
                        <input
                          ref={searchInputRef}
                          type="text"
                          value={searchQuery}
                          onChange={(e) => handleSearch(e.target.value)}
                          placeholder="Suche nach Namen, E-Mails, IDs..."
                          className="block w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        {searchQuery && (
                          <button
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                            onClick={() => handleSearch('')}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {/* Letzte Suchen anzeigen, wenn keine aktive Suche läuft und kein Suchbegriff eingegeben wurde */}
                    {!searchQuery && recentSearches.length > 0 && (
                      <div className="px-4 pb-3">
                        <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Letzte Suchen</h3>
                        <div className="flex flex-wrap gap-2">
                          {recentSearches.map((query, idx) => (
                            <button
                              key={idx}
                              onClick={() => selectRecentSearch(query)}
                              className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-full hover:bg-gray-200 transition-colors"
                            >
                              {query}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Anzeige des Ladestatus */}
                    {isSearching && (
                      <div className="border-t border-gray-100 px-4 py-8 text-center">
                        <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-gray-300 border-t-blue-600"></div>
                        <p className="text-sm text-gray-500 mt-2">Suche läuft...</p>
                      </div>
                    )}
                    
                    {/* Anzeige der Suchergebnisse */}
                    {!isSearching && searchQuery && (
                      <div className="border-t border-gray-100 max-h-[400px] overflow-y-auto">
                        {searchResults.length === 0 ? (
                          <div className="p-4 text-center">
                            <p className="text-sm text-gray-500">Keine Ergebnisse für "{searchQuery}"</p>
                          </div>
                        ) : (
                          <>
                            <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                              <p className="text-xs text-gray-500">{searchResults.length} Ergebnisse gefunden</p>
                            </div>
                            {searchResults.map((result) => (
                              <div 
                                key={`${result.type}-${result.id}`}
                                className="px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer border-b border-gray-100 last:border-b-0"
                                onClick={() => handleSelectSearchResult(result)}
                              >
                                <div className="flex items-center">
                                  <div className="flex-1">
                                    <div className="text-sm font-medium text-gray-900">
                                      {result.firstName} {result.lastName}
                                    </div>
                                    <div className="text-sm text-gray-500 mt-0.5">{result.email}</div>
                                  </div>
                                  <div className="ml-4">
                                    <span className={`
                                      inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                                      ${result.type === 'applicant' ? 'bg-blue-100 text-blue-800' : ''}
                                      ${result.type === 'employee' ? 'bg-green-100 text-green-800' : ''}
                                      ${result.type === 'partner' ? 'bg-purple-100 text-purple-800' : ''}
                                      ${result.type === 'customer' ? 'bg-yellow-100 text-yellow-800' : ''}
                                      ${result.type === 'location' ? 'bg-gray-100 text-gray-800' : ''}
                                    `}>
                                      {result.type === 'applicant' && 'Bewerber'}
                                      {result.type === 'employee' && 'Mitarbeiter'}
                                      {result.type === 'partner' && 'Partner'}
                                      {result.type === 'customer' && 'Kunde'}
                                      {result.type === 'location' && 'Location'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    )}
                    
                    {/* Erweiterte Suche Link */}
                    <div className="p-3 border-t border-gray-100 bg-gray-50">
                      <button 
                        onClick={() => {
                          navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
                          setActiveDropdown(null);
                        }}
                        className="text-sm text-blue-600 hover:text-blue-800 w-full text-center"
                      >
                        Erweiterte Suche
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Notifications */}
            {hasPermission('read:notifications') && (
              <div className="relative" ref={notificationsRef}>
                <button 
                  className={`p-2 rounded-md transition-colors ${
                    activeDropdown === 'notifications' 
                      ? 'text-gray-900 bg-gray-100' 
                      : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                  onClick={() => toggleDropdown('notifications')}
                  aria-expanded={activeDropdown === 'notifications'}
                  aria-haspopup="true"
                >
                  <div className="relative">
                    <Bell className="w-5 h-5" />
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-blue-600 rounded-full text-[10px] font-medium text-white flex items-center justify-center">
                      3
                    </span>
                  </div>
                </button>

                {activeDropdown === 'notifications' && (
                  <div className="absolute right-0 mt-2 w-[380px] bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                    <div className="p-4 border-b border-gray-100">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-gray-900">Benachrichtigungen</h3>
                        <button className="text-sm text-blue-600 hover:text-blue-800">
                          Alle als gelesen markieren
                        </button>
                      </div>
                    </div>
                    <div className="divide-y divide-gray-100 max-h-[480px] overflow-y-auto">
                      {[
                        { 
                          title: 'Neue Bewerbung eingegangen',
                          description: 'Max Mustermann hat sich als Koch beworben',
                          time: 'Vor 5 Min.',
                          unread: true
                        },
                        { 
                          title: 'Abrechnungserinnerung',
                          description: 'Die Monatsabrechnung für Mai ist fällig',
                          time: 'Vor 1 Std.',
                          unread: true
                        },
                        { 
                          title: 'Systemupdate verfügbar',
                          description: 'Eine neue Version ist verfügbar',
                          time: 'Vor 1 Tag',
                          unread: false
                        }
                      ].map((notification, idx) => (
                        <div 
                          key={idx} 
                          className={`px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer
                            ${notification.unread ? 'bg-blue-50/50' : ''}`}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900">
                                {notification.title}
                              </div>
                              <div className="text-sm text-gray-500 mt-0.5">
                                {notification.description}
                              </div>
                              <div className="text-xs text-gray-400 mt-1">
                                {notification.time}
                              </div>
                            </div>
                            {notification.unread && (
                              <div className="w-2 h-2 bg-blue-600 rounded-full mt-1.5" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="p-4 border-t border-gray-100 bg-gray-50">
                      <button 
                        onClick={() => {
                          navigate('/notifications');
                          setActiveDropdown(null);
                        }}
                        className="text-sm text-gray-700 hover:text-gray-900 w-full text-center"
                      >
                        Alle Benachrichtigungen anzeigen
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* User Menu */}
            <div className="relative ml-2" ref={userMenuRef}>
              <button
                onClick={() => toggleDropdown('userMenu')}
                className={`flex items-center space-x-3 px-2 py-1.5 rounded-md transition-colors ${
                  activeDropdown === 'userMenu' 
                    ? 'bg-gray-100' 
                    : 'hover:bg-gray-50'
                }`}
                aria-expanded={activeDropdown === 'userMenu'}
                aria-haspopup="true"
              >
                {user?.picture ? (
                  <img
                    src={user.picture}
                    alt={user?.name || "User"}
                    className="w-8 h-8 rounded-full ring-2 ring-white"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://ui-avatars.com/api/?name=Placeholder&size=200"; // Fallback image
                    }}
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                    <User className="w-4 h-4 text-gray-600" />
                  </div>
                )}
                <div className="hidden sm:flex sm:items-center sm:space-x-2">
                  <span className="text-sm font-medium text-gray-900">{user?.name || "Benutzer"}</span>
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                </div>
              </button>

              {activeDropdown === 'userMenu' && (
                <div className="absolute right-0 mt-2 w-[380px] bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                  {/* User Info Header */}
                  <div className="p-4 bg-gray-50 border-b border-gray-100">
                    <div className="flex items-center space-x-3">
                      {user?.picture ? (
                        <img
                          src={user.picture}
                          alt={user?.name || "User"}
                          className="w-12 h-12 rounded-full ring-2 ring-white"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "https://ui-avatars.com/api/?name=Placeholder&size=200"; // Fallback image
                          }}
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                          <User className="w-6 h-6 text-gray-600" />
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">{user?.name || "Benutzer"}</div>
                        <div className="text-sm text-gray-500">{user?.email || ""}</div>
                      </div>
                    </div>
                  </div>

                  {/* Menu Sections */}
                  <div className="py-2">
                    {profileMenuSections.map((section, sectionIdx) => {
                      // Only show sections where user has access to at least one item
                      const hasAccessToMenuSection = section.items.some(item => 
                        hasPermission(item.permission)
                      );

                      return hasAccessToMenuSection && (
                        <div key={sectionIdx} className="px-2">
                          <div className="px-3 py-2">
                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                              {section.title}
                            </h3>
                          </div>
                          {section.items.map((item, itemIdx) => (
                            hasPermission(item.permission) && (
                              <button
                                key={itemIdx}
                                onClick={() => {
                                  navigate(item.path);
                                  setActiveDropdown(null);
                                }}
                                className="flex items-center space-x-3 px-3 py-2 rounded-md hover:bg-gray-50 transition-colors w-full text-left"
                              >
                                <div className="flex-shrink-0 w-8 h-8 bg-gray-50 text-gray-600 rounded-md flex items-center justify-center">
                                  <item.icon className="w-4 h-4" />
                                </div>
                                <span className="text-sm text-gray-700">
                                  {item.name}
                                </span>
                              </button>
                            )
                          ))}
                          {sectionIdx < profileMenuSections.length - 1 && hasAccessToMenuSection && (
                            <div className="my-2 border-t border-gray-100" />
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Logout Button */}
                  <div className="px-2 py-2 border-t border-gray-100">
                    <button
                      onClick={() => logout({ returnTo: window.location.origin })}
                      className="flex items-center space-x-3 px-3 py-2 rounded-md hover:bg-red-50 transition-colors w-full text-left text-red-600"
                    >
                      <div className="flex-shrink-0 w-8 h-8 bg-red-50 rounded-md flex items-center justify-center">
                        <LogOut className="w-4 h-4" />
                      </div>
                      <span className="text-sm font-medium">Abmelden</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default TopNav;